/**
 * Deliberately boring collision: the player is a vertical cylinder, the world is
 * a list of axis-aligned boxes, and resolution is a fixed number of push-out
 * iterations. No rigid bodies, no solver, no sleeping islands - the same inputs
 * always produce the same outputs, which is the whole point in a race game.
 *
 * Contains no three.js so it can be unit-tested in plain node.
 */

import { clamp, type Vec3 } from '../utils/math';
import type { Box } from './Level';

export interface Collider {
  id: string;
  box: Box;
  /**
   * The glass and the veräjä flip this off once they are out of the way.
   *
   * Doors do not, and it is worth saying why: a door's leaf is a collider that
   * *moves* rather than one that stops existing, so `Door` re-cuts its boxes
   * instead of switching them off. See `doorLeafBoxes`.
   */
  enabled: boolean;
  tag: string;
}

/**
 * The tag on a car's boot lid.
 *
 * `populateCollisionWorld` puts it there and `stepPlayer` reads it back: a lid
 * is the one surface in the venue a jump is *meant* to land on, and landing on
 * one gives the take-off price back. The resolver has to say which surface you
 * ended up on for that to be answerable at all.
 */
export const BOOT_TAG = 'boot';

export interface MoveResult {
  grounded: boolean;
  /** True if the player was pushed out of something horizontally this step. */
  hitWall: boolean;
  /** Speed lost to wall collisions this step, in m/s (used for audio later). */
  impactSpeed: number;
  /**
   * Ids of everything the player was pushed sideways out of this step.
   *
   * Purely observational: nothing in the resolver reads it back, so a caller
   * that ignores it gets the identical movement. It exists because *what* you
   * ran into is sometimes the event - see `LevelDefinition.powerCable` - and
   * the resolver is the only place that knows. Ids can repeat when a contact
   * survives more than one push-out iteration.
   */
  contacts: string[];
  /**
   * The tag of the lid the player ended the step standing on, or null for the
   * world's own ground plane and for a step spent in the air.
   *
   * Observational like `contacts`: nothing in the resolver reads it back. When
   * more than one box grounds you in a step it is the last one, which is also
   * the highest - each grounding contact sets `position.y` to that box's lid,
   * so the last one to fire is the surface you are actually standing on.
   */
  groundTag: string | null;
}

/** Tolerance for "the player is standing exactly on this box's lid". */
const EPS = 1e-3;
const MAX_ITERATIONS = 4;

export class CollisionWorld {
  private readonly colliders: Collider[] = [];

  add(id: string, box: Box, tag = 'static'): Collider {
    const collider: Collider = { id, box, enabled: true, tag };
    this.colliders.push(collider);
    return collider;
  }

  clear(): void {
    this.colliders.length = 0;
  }

  get all(): readonly Collider[] {
    return this.colliders;
  }

  /**
   * Integrates `position` by `velocity * dt` and pushes it back out of the
   * world. Mutates both arguments (hot path, called 120x per second).
   */
  moveCylinder(
    position: Vec3,
    velocity: Vec3,
    radius: number,
    height: number,
    dt: number,
  ): MoveResult {
    const result: MoveResult = {
      grounded: false,
      hitWall: false,
      impactSpeed: 0,
      contacts: [],
      groundTag: null,
    };

    position.x += velocity.x * dt;
    position.z += velocity.z * dt;
    this.resolveHorizontal(position, velocity, radius, height, result);

    const previousY = position.y;
    position.y += velocity.y * dt;
    this.resolveVertical(position, velocity, radius, height, previousY, result);

    return result;
  }

  private resolveHorizontal(
    position: Vec3,
    velocity: Vec3,
    radius: number,
    height: number,
    result: MoveResult,
  ): void {
    for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
      let moved = false;
      for (const collider of this.colliders) {
        if (!collider.enabled) continue;
        const b = collider.box;

        // Vertical overlap test first: standing on a lid must not push sideways.
        if (position.y >= b.maxY - EPS) continue;
        if (position.y + height <= b.minY + EPS) continue;

        const closestX = clamp(position.x, b.minX, b.maxX);
        const closestZ = clamp(position.z, b.minZ, b.maxZ);
        const dx = position.x - closestX;
        const dz = position.z - closestZ;
        const distSq = dx * dx + dz * dz;
        if (distSq >= radius * radius) continue;

        let nx: number;
        let nz: number;
        let penetration: number;

        if (distSq > 1e-12) {
          const dist = Math.sqrt(distSq);
          nx = dx / dist;
          nz = dz / dist;
          penetration = radius - dist;
        } else {
          // Centre is inside the box - eject along the shallowest face.
          const toMinX = position.x - b.minX;
          const toMaxX = b.maxX - position.x;
          const toMinZ = position.z - b.minZ;
          const toMaxZ = b.maxZ - position.z;
          const shallowest = Math.min(toMinX, toMaxX, toMinZ, toMaxZ);
          if (shallowest === toMinX) {
            nx = -1;
            nz = 0;
            penetration = toMinX + radius;
          } else if (shallowest === toMaxX) {
            nx = 1;
            nz = 0;
            penetration = toMaxX + radius;
          } else if (shallowest === toMinZ) {
            nx = 0;
            nz = -1;
            penetration = toMinZ + radius;
          } else {
            nx = 0;
            nz = 1;
            penetration = toMaxZ + radius;
          }
        }

        position.x += nx * penetration;
        position.z += nz * penetration;

        const intoWall = velocity.x * nx + velocity.z * nz;
        if (intoWall < 0) {
          velocity.x -= nx * intoWall;
          velocity.z -= nz * intoWall;
          result.impactSpeed = Math.max(result.impactSpeed, -intoWall);
        }
        moved = true;
        result.hitWall = true;
        result.contacts.push(collider.id);
      }
      if (!moved) break;
    }
  }

  private resolveVertical(
    position: Vec3,
    velocity: Vec3,
    radius: number,
    height: number,
    previousY: number,
    result: MoveResult,
  ): void {
    // Global ground plane at y = 0.
    if (position.y <= 0) {
      position.y = 0;
      if (velocity.y < 0) velocity.y = 0;
      result.grounded = true;
    }

    for (const collider of this.colliders) {
      if (!collider.enabled) continue;
      const b = collider.box;

      // EPS on both sides so resting exactly on a lid still counts as standing
      // on it rather than as "already clear of the box".
      if (position.y - b.maxY >= EPS) continue;
      if (b.minY - (position.y + height) >= EPS) continue;

      const closestX = clamp(position.x, b.minX, b.maxX);
      const closestZ = clamp(position.z, b.minZ, b.maxZ);
      const dx = position.x - closestX;
      const dz = position.z - closestZ;
      if (dx * dx + dz * dz >= radius * radius) continue;

      // Only resolve vertically if we actually crossed a face this step.
      //
      // Without this guard, a player merely brushing a wall (a hair of leftover
      // horizontal overlap after the push-out) picks whichever ejection is
      // "shallower" - and for a tall wall that is *downwards*, which drops them
      // under the floor and turns them into a ghost that walks through the
      // whole level. Standing next to something must never move you in Y.
      const cameFromAbove = previousY >= b.maxY - EPS;
      const cameFromBelow = previousY + height <= b.minY + EPS;

      if (cameFromAbove && velocity.y <= 0) {
        position.y = b.maxY;
        velocity.y = 0;
        result.grounded = true;
        result.groundTag = collider.tag;
      } else if (cameFromBelow && velocity.y > 0) {
        position.y = b.minY - height;
        velocity.y = 0;
      }
    }
  }

  /** True if a cylinder placed here would overlap anything solid. */
  overlaps(position: Vec3, radius: number, height: number): boolean {
    for (const collider of this.colliders) {
      if (!collider.enabled) continue;
      const b = collider.box;
      if (position.y >= b.maxY - EPS) continue;
      if (position.y + height <= b.minY + EPS) continue;
      const closestX = clamp(position.x, b.minX, b.maxX);
      const closestZ = clamp(position.z, b.minZ, b.maxZ);
      const dx = position.x - closestX;
      const dz = position.z - closestZ;
      if (dx * dx + dz * dz < radius * radius) return true;
    }
    return false;
  }
}
